import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiMinus,
  FiX,
  FiTool,
  FiCalendar
} from 'react-icons/fi';
import './ScheduleCalendar.css';
import Api from '../../Api'; // Adjust path as needed

const ScheduleCalendar = forwardRef(({ employee, onClose, fullScheduleData }, ref) => {
  // --- STATE MANAGEMENT ---

  // 1. Initial Date based on provided data
  const [currentDate, setCurrentDate] = useState(() => {
    // If overrides exist, use the first date found
    if (fullScheduleData?.overrides) {
      try {
        const overrides = typeof fullScheduleData.overrides === 'string'
          ? JSON.parse(fullScheduleData.overrides)
          : fullScheduleData.overrides;
        
        const keys = Object.keys(overrides || {});
        if (keys.length > 0) {
          const [y, m] = keys[0].split('-').map(Number); // "2026-03-xx" -> y=2026, m=3
          return new Date(y, m - 1, 1); // Date uses 0-based month
        }
      } catch (e) {
        console.error("Error calculating initial date:", e);
      }
    }
    return new Date();
  });

  const [scheduleOverrides, setScheduleOverrides] = useState(() => {
    // console.log('Initializing scheduleOverrides from fullScheduleData:', fullScheduleData);
    // Initialize from props if available
    if (fullScheduleData && fullScheduleData.overrides) {
        let overrides = fullScheduleData.overrides;
        if (typeof overrides === 'string') {
            try {
                overrides = JSON.parse(overrides);
            } catch (e) {
                overrides = {};
            }
        }
        return overrides;
    }
    return {};
  });
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Sync ONLY when ID changes to avoid overwriting local changes on re-renders
  useEffect(() => {
      if (fullScheduleData && fullScheduleData.overrides) {
        let newOverrides = fullScheduleData.overrides;
        if (typeof newOverrides === 'string') {
            try {
                newOverrides = JSON.parse(newOverrides);
            } catch (e) {
                newOverrides = {};
            }
        }
        setScheduleOverrides(newOverrides);
      } else {
        // Only clear if we really changed context, but for now let's trust ID check
        setScheduleOverrides({});
      }
  }, [fullScheduleData?.id, fullScheduleData?.employee?.id]);

  // 3. Expose data to parent via ref
  useImperativeHandle(ref, () => ({
    getScheduleData: () => ({
      employee_id: employee.id,
      month: currentDate.getMonth(), // 0-11
      year: currentDate.getFullYear(),
      overrides: scheduleOverrides,
      checked: true
    })
  }));
  
  // 4. Load schedule data when month/year changes
  useEffect(() => {
    let isMounted = true;
    
    const fetchSchedule = async () => {
      try {
        if (!employee?.id) return;
        
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth(); // 0-11
        
        // Check if the current props (fullScheduleData) already match the requested month/year
        // If so, we might not need to fetch, BUT props might be stale if we navigated away and came back.
        // Actually, the issue is that we might be relying on props too much.
        // If we navigate to April, and props are for March, we MUST fetch.
        
        // Let's check what props we have
        let propsMatch = false;
        if (fullScheduleData?.overrides) {
             const keys = Object.keys(typeof fullScheduleData.overrides === 'string' ? JSON.parse(fullScheduleData.overrides) : fullScheduleData.overrides);
             if (keys.length > 0) {
                 const [py, pm] = keys[0].split('-').map(Number);
                 if (py === year && (pm - 1) === month) {
                     propsMatch = true;
                 }
             }
        }
        
        // If props match the current view, use them and DO NOT fetch (to save bandwidth and avoid flicker)
        if (propsMatch) {
             // Ensure state is synced with these matching props (handled by the other useEffect, but let's be sure)
             // Actually, the other useEffect handles prop changes. 
             // If we just navigated to a month that matches props, we should use props.
             // console.log('Using props for current view (match found)');
             return; 
        }

        // Always fetch from API if props don't match the current view
        // console.log(`Fetching schedule for: ${year}-${month + 1}`);
        const response = await Api.get(`/schedules/${employee.id}/${year}/${month}`);
        
        if (isMounted) {
            // Api.get returns { data: ... } so we need to access response.data
            const responseData = response.data || response;
            let loadedOverrides = (responseData && responseData.overrides) ? responseData.overrides : {};
            
            if (typeof loadedOverrides === 'string') {
              try { loadedOverrides = JSON.parse(loadedOverrides); } catch (e) {}
            }
            setScheduleOverrides(loadedOverrides);
        }
      } catch (error) {
        if (isMounted) {
            // If error (e.g. 404 not found), clear overrides for this month
            setScheduleOverrides({});
        }
        // 404 is expected if no schedule exists yet
        if (error.response && error.response.status !== 404) {
           console.error('Error loading schedule:', error);
        }
      }
    };
    
    fetchSchedule();
    return () => { isMounted = false; };
  }, [currentDate, employee, fullScheduleData]); // Run whenever date or employee changes

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const daysOfWeek = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // 1. Pre-process and normalize overrides into a Map for infallible lookup
    // This handles keys like "2026-3-12" vs "2026-03-12" automatically
    const overridesMap = new Map();
    // Use state directly. Fallback is handled in handleChangeStatus if needed.
    // Relying on fallback here masks empty state issues.
    let overridesSource = scheduleOverrides;
    
    // Safety: if state is REALLY empty but we have props, we can use props for DISPLAY ONLY
    // But this shouldn't be needed if sync/hydration works. 
    // Keeping it for read-only safety, but edit logic must hydrate.
    if ((!overridesSource || Object.keys(overridesSource).length === 0) && fullScheduleData && fullScheduleData.overrides) {
        overridesSource = fullScheduleData.overrides;
    }

    if (overridesSource) {
        // Ensure it's an object if it's a string
        let overridesObj = overridesSource;
        if (typeof overridesObj === 'string') {
             try { overridesObj = JSON.parse(overridesObj); } catch(e) { overridesObj = {}; }
        }

        Object.entries(overridesObj).forEach(([key, value]) => {
            if (!key) return;
            try {
                // Split by '-' or just try to parse date
                // Assuming format YYYY-MM-DD or similar
                const parts = key.split('-').map(Number);
                if (parts.length === 3) {
                    // Normalize to YYYY-MM-DD (e.g. 2026-03-11)
                    // Ensure month and day are padded
                    const normKey = `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`;
                    overridesMap.set(normKey, value);
                    // console.log(`Mapped Override: ${key} -> ${normKey} = ${value}`);
                }
            } catch (e) {
                console.error('Error normalizing override key:', key, e);
            }
        });
    }
    
    // console.log('Final Overrides Map Size:', overridesMap.size);

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month padding
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ day: '', type: 'padding' });
    }
    
    // Scale Logic Setup
    const scheduleName = fullScheduleData?.schedule?.toLowerCase() || '';
    const admissionDateStr = fullScheduleData?.original?.admission_date;
    const admissionDate = admissionDateStr ? new Date(admissionDateStr) : new Date(year, 0, 1); // Fallback date
    
    // Reset hours for accurate day diff calculation
    admissionDate.setHours(0, 0, 0, 0);

    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      const currentDayDate = new Date(year, month, i);
      currentDayDate.setHours(0, 0, 0, 0);
      
      // Generate key in exact YYYY-MM-DD format
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      let status = 'FOLGA'; // Default

      // Check overrides using the normalized map
      let hasOverride = false;
      
      if (overridesMap.has(dateKey)) {
          hasOverride = true;
          status = overridesMap.get(dateKey);
          // console.log(`Override Applied: ${dateKey} -> ${status}`);
      }
      
      if (!hasOverride) {
        // 2. Calculate based on Schedule Type
        if (scheduleName.includes('12x36')) {
        // Calculate difference in days from admission (or fallback)
        const diffTime = currentDayDate.getTime() - admissionDate.getTime();
        const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
        
        // Even days from admission are work days, odd are off (assuming work starts on admission)
        if (diffDays % 2 === 0) {
          status = 'TRAB.';
        } else {
          status = 'FOLGA';
        }
      } 
      else if (scheduleName.includes('5x2') || scheduleName.includes('segunda a sexta') || scheduleName.includes('administrativo')) {
        const dayOfWeek = currentDayDate.getDay(); // 0 = Sun, 6 = Sat
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          status = 'FOLGA';
        } else {
          status = 'TRAB.';
        }
      }
      else if (scheduleName.includes('6x1') || scheduleName.includes('segunda a sabado') || scheduleName.includes('segunda a sábado')) {
        const dayOfWeek = currentDayDate.getDay(); // 0 = Sun
        if (dayOfWeek === 0) {
          status = 'FOLGA';
        } else {
          status = 'TRAB.';
        }
      }
      else {
        // Fallback for unknown schedules (assume Mon-Fri work)
        const dayOfWeek = currentDayDate.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          status = 'FOLGA';
        } else {
          status = 'TRAB.';
        }
      }
      }

      days.push({
        day: i,
        month,
        year,
        dateKey,
        type: 'day',
        status,
        hasOverride, // Flag to indicate manual override
        isToday: i === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear()
      });
    }
    
    // Next month padding to fill the grid (optional, usually 35 or 42 cells)
    while (days.length % 7 !== 0) {
      days.push({ day: '', type: 'padding' });
    }
    
    return days;
  }, [currentDate, scheduleOverrides, fullScheduleData]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setActiveDropdown(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setActiveDropdown(null);
  };
  
  const handleDayClick = (item) => {
    if (!item.day) return;
    
    // Toggle dropdown
    if (activeDropdown && activeDropdown.dateKey === item.dateKey) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(item);
    }
  };

  const handleChangeStatus = (item, newStatus, e) => {
    e.stopPropagation();
    
    // Start with current state
    let currentOverrides = { ...scheduleOverrides };
    
    // CRITICAL FIX: If state is empty but props have data, hydrate from props first!
    // This prevents losing existing overrides if the user edits before state is fully synced or if state was empty
    // ALSO: Only hydrate if the props data actually belongs to the CURRENT VIEW month/year
    if (Object.keys(currentOverrides).length === 0 && fullScheduleData && fullScheduleData.overrides) {
        try {
            const propOverrides = typeof fullScheduleData.overrides === 'string'
                ? JSON.parse(fullScheduleData.overrides)
                : fullScheduleData.overrides;
            
            // Check if props data matches current view date
            // We assume keys are YYYY-MM-DD. We check if at least one key matches current year/month.
            // If props overrides are empty, it doesn't matter.
            const keys = Object.keys(propOverrides);
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // 1-based
            
            // CRITICAL: Only hydrate if the props actually match the CURRENT VIEWED MONTH
            // If the props overrides contain data for March, but we are looking at April,
            // we MUST NOT hydrate, because those overrides are for March!
            const isMatchingData = keys.some(key => {
                const parts = key.split('-').map(Number);
                // Check if key year/month matches current view year/month
                return parts.length === 3 && parts[0] === currentYear && parts[1] === currentMonth;
            });
            
            if (isMatchingData) {
                // Merge props into currentOverrides ONLY if they match the current month
                // Filter props to only include current month overrides to be safe
                const filteredProps = {};
                keys.forEach(k => {
                    const parts = k.split('-').map(Number);
                    if (parts.length === 3 && parts[0] === currentYear && parts[1] === currentMonth) {
                        filteredProps[k] = propOverrides[k];
                    }
                });
                currentOverrides = { ...currentOverrides, ...filteredProps };
            }
        } catch (e) {
            console.error('Error hydrating overrides from props on edit:', e);
        }
    }
    
    // Add new status with normalized key
    currentOverrides[item.dateKey] = newStatus;
    
    setScheduleOverrides(currentOverrides);
    setActiveDropdown(null);
  };

  const admissionDate = fullScheduleData?.original?.admission_date 
    ? new Date(fullScheduleData.original.admission_date).toLocaleDateString('pt-BR') 
    : 'N/A';

  // Calculate totals for current month
  const { shiftsCount, daysOffCount } = useMemo(() => {
    let shifts = 0;
    let daysOff = 0;
    
    calendarDays.forEach(day => {
      if (day.type === 'day') {
        if (day.status === 'TRAB.') shifts++;
        else if (day.status === 'FOLGA') daysOff++;
      }
    });
    
    return { shiftsCount: shifts, daysOffCount: daysOff };
  }, [calendarDays]);

  const handleInvertSchedule = () => {
    // Determine current month days based on 12x36 logic to invert them
    const newOverrides = { ...scheduleOverrides };
    const scheduleName = fullScheduleData?.schedule?.toLowerCase() || '';
    
    if (scheduleName.includes('12x36')) {
        calendarDays.forEach(day => {
            if (day.type === 'day') {
                const currentStatus = day.status;
                const newStatus = currentStatus === 'TRAB.' ? 'FOLGA' : 'TRAB.';
                newOverrides[day.dateKey] = newStatus;
            }
        });
        setScheduleOverrides(newOverrides);
    }
  };

  const is12x36 = fullScheduleData?.schedule?.toLowerCase().includes('12x36');

  return (
    <div className="schedule-calendar-container">
      {/* DEBUG PANEL - REMOVE LATER */}
      {/* <div style={{ background: '#f0f0f0', padding: '10px', marginBottom: '10px', fontSize: '10px', overflow: 'auto', maxHeight: '100px' }}>
        <strong>DEBUG:</strong><br/>
        Emp ID: {employee?.id}<br/>
        Date: {currentDate.toISOString()}<br/>
        Overrides Count: {Object.keys(scheduleOverrides).length}<br/>
        Overrides: {JSON.stringify(scheduleOverrides)}
      </div> */}

      {/* Header */}
      <div className="sc-header">
        <div className="sc-user-info">
          <div className="sc-avatar">
            {employee?.photo ? (
              <img src={employee.photo} alt={employee.name} />
            ) : (
              <span className="sc-avatar-initials">
                {employee?.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="sc-user-details">
            <h2>{employee?.name}</h2>
            <div className="sc-meta">
              <span className="sc-role-badge">
                {employee?.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="sc-info-grid">
        <div className="sc-info-item">
          <span className="sc-label">Empresa</span>
          <span className="sc-value" title={fullScheduleData?.company?.trade_name || fullScheduleData?.company?.name}>
            {fullScheduleData?.company?.trade_name || fullScheduleData?.company?.name || 'N/A'}
          </span>
        </div>
        
        <div className="sc-info-item">
          <span className="sc-label">Posto de Serviço</span>
          <span className="sc-value" title={fullScheduleData?.station?.name}>
            {fullScheduleData?.station?.name || 'N/A'}
          </span>
        </div>
        
        <div className="sc-info-item sc-info-item-full">
          <span className="sc-label">Jornada</span>
          <span className="sc-value">
            {fullScheduleData?.schedule || 'N/A'}
          </span>
        </div>

        <div className="sc-info-item">
          <span className="sc-label">Admissão</span>
          <span className="sc-value">{admissionDate}</span>
        </div>
        
        <div className="sc-info-item">
          <span className="sc-label">Escalas</span>
          <span className="sc-value green">{shiftsCount}</span>
        </div>
        
        <div className="sc-info-item">
          <span className="sc-label">Folgas</span>
          <span className="sc-value red">{daysOffCount}</span>
        </div>
        
        <div className="sc-info-item">
          <span className="sc-label">Conferido</span>
          <span className={`sc-status-badge ${fullScheduleData?.checked ? 'yes' : 'no'}`}>
            {fullScheduleData?.checked ? 'Sim' : 'Não'}
          </span>
        </div>
      </div>

      {/* Calendar */}
      <div className="sc-calendar">
        <div className="sc-cal-header">
          <h3 className="sc-cal-title">
            <FiCalendar /> {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            {is12x36 && (
              <button 
                onClick={handleInvertSchedule}
                className="sc-invert-btn"
                title="Inverter escala para o mês atual"
              >
                Inverter Escala
              </button>
            )}
          </h3>
          <div className="sc-cal-nav">
            <button onClick={handlePrevMonth} className="sc-nav-btn"><FiChevronLeft /></button>
            <button onClick={handleNextMonth} className="sc-nav-btn"><FiChevronRight /></button>
          </div>
        </div>
        
        <div className="sc-cal-grid-header">
          {daysOfWeek.map(day => (
            <div key={day} className="sc-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="sc-cal-grid">
          {calendarDays.map((item, index) => (
            <div 
              key={`${item.dateKey}-${index}-${item.status}`} 
              className={`
                sc-day-cell
                ${item.type === 'padding' ? 'padding' : ''}
                ${item.isToday ? 'today' : ''}
                ${item.hasOverride ? 'overridden' : ''}
                ${activeDropdown?.dateKey === item.dateKey && item.type !== 'padding' ? 'active' : ''}
              `}
              onClick={() => handleDayClick(item)}
            >
              {item.day && (
                <>
                  <span className={`sc-day-number ${item.status === 'TRAB.' ? 'work' : 'off'}`}>
                    {item.day}
                  </span>
                  <div 
                    className={`sc-day-status ${item.status === 'TRAB.' ? 'work' : 'off'}`}
                    title={item.status}
                  >
                    {item.status}
                  </div>
                  
                  {activeDropdown?.dateKey === item.dateKey && (
                    <div className="sc-day-dropdown" onClick={e => e.stopPropagation()}>
                      <button 
                        className="sc-dropdown-item work"
                        onClick={(e) => handleChangeStatus(item, 'TRAB.', e)}
                      >
                        TRABALHO
                      </button>
                      <button 
                        className="sc-dropdown-item off"
                        onClick={(e) => handleChangeStatus(item, 'FOLGA', e)}
                      >
                        FOLGA
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default ScheduleCalendar;
